import React from "react";
import {
  TextField,
  Grid,
  Autocomplete,
  InputAdornment,
  IconButton,
} from "@mui/material";
import { useStyles } from "./styles";
import { ErrorMessage, Formik } from "formik";
import * as Yup from "yup";
import useClients from "../../../hooks/store/clients";
import { useLocation, useNavigate } from "react-router-dom";
import useUser from "../../../hooks/store/user";
import useBusiness from "../../../hooks/store/business";
import Loader from "../../../common/Loader";
import { normalizeRole } from "../../../access/roles";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import usePermissions from "../../../hooks/usePermissions";
import FormPageLayout from "../../../common/forms/FormPageLayout";
import FormikTextField from "../../../common/forms/FormikTextField";
import SubmitCancelActions from "../../../common/forms/SubmitCancelActions";

const getSelectedRole = (roleOption) => normalizeRole(roleOption?.roleName, null);

const UserAddSchema = Yup.object().shape({
  idRole: Yup.object().required("* Please select role"),
  name: Yup.string()
    .trim()
    .required("* Please enter name")
    .max(50, "Name cannot exceed 50 characters"),
  email: Yup.string()
    .email("* Please enter a valid email")
    .required("* Please enter email"),
  password: Yup.string().max(255).required("Password is required"),
  clientId: Yup.object().when("idRole", {
    is: (role) => {
      const selectedRole = getSelectedRole(role);
      return selectedRole === "Client" || selectedRole === "Business";
    },
    then: Yup.object().required("* Please select a client"),
    otherwise: Yup.object().nullable(),
  }),
  businessId: Yup.object().when(["idRole", "clientId"], {
    is: (role, client) => getSelectedRole(role) === "Business" && client != null,
    then: Yup.object().required("* Please select a business"),
    otherwise: Yup.object().nullable(),
  }),
});
const UserEditSchema = Yup.object().shape({
  id: Yup.string().trim().required("* Please enter name"),
  name: Yup.string()
    .trim()
    .required("* Please enter name")
    .max(50, "name cannot exceed 50 characters"),
  // email: Yup.string()
  //   .email("* Please enter valid email")
  //   .required("* Please enter email"),
});

const initValues = {
  id: "",
  name: "",
  email: "",
  clientId: "",
  businessId: "",
  idRole: "",
  password: "",
  // phone: "",
  // address: "",
  // status: false,
};

function Index() {
  const location = useLocation();
  const classes = useStyles();
  const navigate = useNavigate();
  const isEditRoute = location.pathname === "/edit/users";
  const editingUser = location.state?.users || null;
  const hasEditableUser = Boolean(editingUser?.id);
  const {
    addUserDataAction,
    updateUserDataAction,
    rolesDataAction,
    roles,
    loading,
    addUsersWithClientAction,
  } = useUser();
  const {
    clientId,
    getClientIdDataAction,
    rolesClientAction,
    rowsPerPage,
    page,
  } = useClients();
  const { getBusinessByClientAction, businessData } = useBusiness();
  const { clientId: scopedClientId, isClient } = usePermissions();
  const [showPassword, setShowPassword] = React.useState(false);

  React.useEffect(() => {
    if (isEditRoute && !hasEditableUser) {
      navigate("/users", { replace: true });
    }
  }, [hasEditableUser, isEditRoute, navigate]);

  React.useEffect(() => {
    if (isEditRoute && !hasEditableUser) {
      return undefined;
    }

    const abortController = new AbortController();
    rolesDataAction();
    if (isClient && scopedClientId) {
      rolesClientAction({
        clientIdLogin: scopedClientId,
        limit: rowsPerPage,
        offSet: rowsPerPage * page,
        signal: abortController.signal,
      });
    } else {
      getClientIdDataAction();
    }
    return () => {
      abortController.abort();
    };
  }, [
    getClientIdDataAction,
    page,
    isClient,
    rolesClientAction,
    rolesDataAction,
    rowsPerPage,
    scopedClientId,
    hasEditableUser,
    isEditRoute,
  ]);

  const edit = isEditRoute && hasEditableUser;
  const title = edit ? "Edit" : "Add New";

  const initialFormValues = React.useMemo(() => {
    if (edit && editingUser) {
      return {
        ...initValues,
        id: editingUser?.id || "",
        name: editingUser?.name || "",
        email: editingUser?.email || "",
      };
    }

    return { ...initValues };
  }, [edit, editingUser]);

  const handleGetBusinessByClient = (params) => {
    if (isClient && scopedClientId) {
      getBusinessByClientAction({
        id: scopedClientId,
      });
    } else {
      getBusinessByClientAction({
        id: params?.id,
      });
    }
  };

  if (isEditRoute && !hasEditableUser) {
    return null;
  }

  return (
    <>
      {loading && <Loader />}
      <Formik
        validationSchema={edit ? UserEditSchema : UserAddSchema}
        initialValues={initialFormValues}
        enableReinitialize
        validateOnBlur={false}
        validateOnChange={false}
        onSubmit={async (values) => {
          let action;

          if (edit) {
            action = await updateUserDataAction({
              id: editingUser.id,
              name: values.name,
              status: "Active",
            });
          } else if (isClient && scopedClientId) {
            action = await addUsersWithClientAction({
              name: values.name,
              email: values.email,
              idRole: values?.idRole?.id,
              clientId: values?.clientId?.id,
              businessId: values?.businessId?.id,
              clientIdLogin: scopedClientId,
              password: values?.password,
            });
          } else {
            action = await addUserDataAction({
              name: values.name,
              email: values.email,
              idRole: values?.idRole?.id,
              clientId: values?.clientId?.id,
              businessId: values?.businessId?.id,
              password: values?.password,
            });
          }

          if (action?.meta?.requestStatus === "fulfilled") {
            navigate("/users");
          }
        }}
      >
        {(formik) => {
          const {
            errors,
            touched,
            handleChange,
            handleBlur,
            handleSubmit,
            values,
            setFieldValue,
          } = formik;
          const textFieldProps = {
            values,
            handleChange,
            handleBlur,
            errors,
            touched,
            className: classes.textfield,
            inputErrorClassName: classes.inputError,
            inputNoErrorClassName: classes.inputNoError,
            errorClassName: classes.error,
          };

          return (
            <FormPageLayout
              title={`${title} Users`}
              cardTitle="Users Details"
              formProps={{ onSubmit: handleSubmit }}
              cardClassName={classes.Card}
              headerClassName={classes.heading}
              actions={
                <SubmitCancelActions
                  buttonClassName={classes.button}
                  onCancel={() => navigate("/users")}
                  submitLabel={`${title} Users`}
                />
              }
            >
              <Grid container spacing={2} justifyContent="start">
                {edit ? (
                  <Grid item xs={12} sm={6}>
                    <FormikTextField
                      {...textFieldProps}
                      name="id"
                      label="Id"
                      disabled
                    />
                  </Grid>
                ) : (
                  <Grid item xs={12} sm={6} md={6}>
                    <Autocomplete
                      name="idRole"
                      options={
                        (isClient
                          ? roles?.filter(
                              (role) =>
                                normalizeRole(role?.roleName, null) !== "Admin"
                            )
                          : roles) || []
                      }
                      getOptionLabel={(option) =>
                        normalizeRole(option?.roleName, "")
                      }
                      noOptionsText="No Roles found"
                      onChange={(_, newValue) => {
                        if (newValue) {
                          setFieldValue("idRole", newValue);
                          setFieldValue("businessId", "");
                          setFieldValue("clientId", "");
                          return;
                        }

                        setFieldValue("idRole", "");
                      }}
                      onBlur={handleBlur}
                      value={values?.idRole || {}}
                      renderInput={(params) => (
                        <TextField {...params} label="Select Role" />
                      )}
                    />
                    <ErrorMessage
                      name="idRole"
                      component="div"
                      className={`${classes.error} ${classes.errorMargingTop}`}
                    />
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <FormikTextField
                    {...textFieldProps}
                    name="name"
                    label="Name"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FormikTextField
                    {...textFieldProps}
                    name="email"
                    label="Email Address"
                    disabled={edit}
                  />
                </Grid>
                {!edit ? (
                  <Grid item xs={12} sm={6}>
                    <FormikTextField
                      {...textFieldProps}
                      fullWidth
                      name="password"
                      label="Password"
                      type={showPassword ? "text" : "password"}
                      InputProps={{
                        endAdornment: (
                          <InputAdornment position="end">
                            <IconButton
                              onClick={() => setShowPassword(!showPassword)}
                              edge="end"
                            >
                              {showPassword ? <Visibility /> : <VisibilityOff />}
                            </IconButton>
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Grid>
                ) : null}
                {["Client", "Business"].includes(getSelectedRole(values?.idRole)) ? (
                  <Grid item xs={12} sm={6} md={6}>
                    <Autocomplete
                      name="clientId"
                      options={clientId || []}
                      getOptionLabel={(option) => option.name || ""}
                      noOptionsText="No Client Found"
                      onChange={(_, newValue) => {
                        if (newValue) {
                          setFieldValue("clientId", newValue);

                          if (getSelectedRole(values?.idRole) === "Business") {
                            handleGetBusinessByClient(newValue);
                            setFieldValue("businessId", "");
                          }

                          return;
                        }

                        setFieldValue("clientId", "");
                      }}
                      onBlur={handleBlur}
                      value={values?.clientId || {}}
                      renderInput={(params) => (
                        <TextField {...params} label="Select Client" />
                      )}
                    />
                    <ErrorMessage
                      name="clientId"
                      component="div"
                      className={`${classes.error} ${classes.errorMargingTop}`}
                    />
                  </Grid>
                ) : null}
                {getSelectedRole(values?.idRole) === "Business" &&
                values?.clientId ? (
                  <Grid item xs={12} sm={6} md={6}>
                    <Autocomplete
                      name="businessId"
                      options={businessData || []}
                      getOptionLabel={(option) => option.name || ""}
                      noOptionsText="No Business Found"
                      onChange={(_, newValue) => {
                        if (newValue) {
                          setFieldValue("businessId", newValue);
                          return;
                        }

                        setFieldValue("businessId", "");
                      }}
                      onBlur={handleBlur}
                      value={values?.businessId || {}}
                      renderInput={(params) => (
                        <TextField {...params} label="Select Business" />
                      )}
                    />
                    <ErrorMessage
                      name="businessId"
                      component="div"
                      className={`${classes.error} ${classes.errorMargingTop}`}
                    />
                  </Grid>
                ) : null}
              </Grid>
            </FormPageLayout>
          );
        }}
      </Formik>
    </>
  );
}

export default Index;
